import React, {useState} from 'react';
import {useControllerProps} from '../../Widget/ControllerContext';
import {Text, TextButton, TextButtonPriority, TextPriority} from '@wix/wix-ui-tpa/cssVars';
import {ChevronDown, ChevronUp} from '@wix/wix-ui-icons-common/on-stage';
import {classes} from './SubscriptionsProjections.st.css';
import {useTranslation, useExperiments, Trans} from '@wix/yoshi-flow-editor';
import {NUMBER_OF_PROJECTIONS_TO_SHOW_BEFORE_COLLAPSE, SPECS} from '../../../../common/constants';
import {useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {clickOnShowMoreBi} from '../../../../types/checkoutApp.types';
import {CheckoutOrigin} from '../../../../domain/utils/common/bi.util.common';
import {SkeletonLineWrapper} from '../../../../common/components/Skeleton/SkeletonLineWrapper/SkeletonLineWrapper';
import {SubscriptionChargeModel} from '../../../../domain/models/checkout/CartSummary/SubscriptionChargeModel';

export enum SubscriptionsProjectionsDataHooks {
  root = 'SubscriptionsProjectionsDataHooks.root',
  projectionText = 'SubscriptionsProjectionsDataHooks.projectionText',
  CollapseExpandButton = 'SubscriptionsProjectionsDataHooks.collapseExpandButton',
  Loader = 'SubscriptionsProjectionsDataHooks.Loader',
}

export const SubscriptionsProjections = () => {
  const {
    checkoutStore: {cartSummary, reportClickToOpenCloseMoreDetails},
  } = useControllerProps();
  const localeKeys = useLocaleKeys();

  const projections = cartSummary!.paymentSummary.subscriptionCharges;

  const isCollapsable = projections.length > NUMBER_OF_PROJECTIONS_TO_SHOW_BEFORE_COLLAPSE;

  const [isCollapsed, setIsCollapsed] = useState(true);
  const onCollapseExpandClick = () => {
    reportClickToOpenCloseMoreDetails({
      action: isCollapsed ? clickOnShowMoreBi.OPEN : clickOnShowMoreBi.CLOSE,
      origin: CheckoutOrigin.checkoutProjections,
    });

    setIsCollapsed(!isCollapsed);
  };

  if (projections.length === 0) {
    return null;
  }

  const projectionsToShow = projections.slice(
    0,
    isCollapsed ? NUMBER_OF_PROJECTIONS_TO_SHOW_BEFORE_COLLAPSE : projections.length
  );

  return (
    <div data-hook={SubscriptionsProjectionsDataHooks.root} className={classes.root}>
      {projectionsToShow.map((projection, index) => (
        <SubscriptionProjection key={index} showItemName={projections.length > 1} projection={projection} />
      ))}

      {isCollapsable && (
        <TextButton
          className={classes.collapseButton}
          suffixIcon={isCollapsed ? <ChevronDown /> : <ChevronUp />}
          priority={TextButtonPriority.secondary}
          data-hook={SubscriptionsProjectionsDataHooks.CollapseExpandButton}
          onClick={onCollapseExpandClick}>
          {isCollapsed
            ? localeKeys.checkout.subscriptionProjection.showMore()
            : localeKeys.checkout.subscriptionProjection.showLess()}
        </TextButton>
      )}
    </div>
  );
};

const getItemNameDisplay = (projection: SubscriptionChargeModel, showAllNames: boolean): string => {
  return showAllNames ? projection.lineItemNames.join(', ') : projection.lineItemName;
};

export const SubscriptionProjection = ({
  showItemName,
  projection,
}: {
  showItemName: boolean;
  projection: SubscriptionChargeModel;
}) => {
  const localeKeys = useLocaleKeys();
  const {t} = useTranslation();
  const {experiments} = useExperiments();
  const {
    checkoutStore: {isCartSummaryUpdating},
  } = useControllerProps();

  const showAllNames = experiments.enabled(SPECS.ShowAllSubscriptionItemNames);

  if (isCartSummaryUpdating) {
    return (
      <div data-hook={SubscriptionsProjectionsDataHooks.Loader} className={classes.projectionLoader}>
        <SkeletonLineWrapper size="large" />
      </div>
    );
  }

  const itemNameDisplay = getItemNameDisplay(projection, showAllNames);

  return (
    <Text
      priority={TextPriority.secondary}
      className={classes.projectionText}
      data-hook={SubscriptionsProjectionsDataHooks.projectionText}>
      {showItemName ? (
        <Trans t={t} components={{1: <b />}}>
          {localeKeys.checkout.order_summary.totals.pricingPlanProjections.label.$value({
            itemName: itemNameDisplay,
            description: projection.description,
          })}
        </Trans>
      ) : (
        projection.description
      )}
    </Text>
  );
};
